import React, { useState, useEffect, useRef, ReactNode } from "react";
import { useSelector, useDispatch } from "react-redux";
import classNames from "classnames";
import {
  Switch,
  Route,
  Link,
  useHistory,
  useRouteMatch,
  useParams,
} from "react-router-dom";

import { RootState } from "src/redux/reducers";
import { CurrentViewValue } from "./types";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import AnimatedDiv from "src/app/components/AnimatedDiv/AnimatedDiv";
import { TopbarRight } from "./components/TopbarRight";
import {
  Breadcrumbs,
  BreadcrumbsItem,
} from "../../components/Breadcrumbs/Breadcrumbs";
import { ReactComponent as PlusIcon } from "src/images/plus-transparent.svg";
import { ProjectCover } from "./components/ProjectCover";
import { ProjectDetails } from "src/app/project-details/ProjectDetails";
import useCheckProjectPermission from "src/hooks/useCheckProjectPermission";
import { Button } from "src/app/components/Button/Button";
import { AppLocation } from "src/redux/main/mainReducer";
import SingleProjectOverviewTableWrapper from "../SingleProjectOverviewTableWrapper";
import { ProjectReportDropdown } from "./dropdowns/ProjectReportDropdown";
import { CampaignDataDropdown } from "./dropdowns/CampaignDataDropdown";
import { ExtensionsDropdown } from "src/app/dropdowns/ExtensionsDropdown/ExtensionsDropdown";
import { ProjectMenuDropdown } from "./dropdowns/ProjectMenuDropdown";
import TasksTableView from "./TasksTable/TasksTableView";
import CreatorSearchView from "./CreatorSearchTable/CreatorSearchView";
import {
  clearProjectBasicData,
  clearTasksCounters,
  getProject,
  getTasksCounters,
  setOpenAddBriefPane,
} from "src/redux";
import SingleProjectTopbar from "./components/SingleProjectTopbar";
import AddAIBriefPaneExistingProject from "src/app/modals/CreateNewProjectPane/AddAIBriefPaneExistingProject";
import WebsocketListener from "src/WebsocketListener";
import { useHasAccessAndIsEnabledCreatorSearch } from "../hooks/useHasAccessAndIsEnabledCreatorSearch";
import { TaskType } from "src/types";
import { usePageTitle } from "src/app/title-management/usePageTitle";
import { capitalizeFirstLetter } from "src/utils/methods";
import SingleProjectModals from "./components/SingleProjectModals";

import "./SingleProject.scss";

export default function SingleProject() {
  const [currentView, setCurrentView] = useState<CurrentViewValue>("list");
  const [showCreatorSearchTab, setShowCreatorSearchTab] =
    useState<boolean>(false);
  const [boardHidden, setBoardHidden] = useState(true);
  const [isActivateProjectPaneOpen, setisActivateProjectPaneOpen] =
    useState(false);
  const [showMemberOrClientModal, setShowMemberOrClientModal] =
    useState<boolean>(false);
  const [showAddProjectFieldModal, setShowAddProjectFieldModal] =
    useState<boolean>(false);

  const singleProjectRef = useRef<HTMLDivElement>(null);

  const { projectId }: { projectId: string } = useParams();
  const history = useHistory();
  const { path } = useRouteMatch();
  const dispatch = useDispatch();

  const hasProjectMetaFieldsManagementPermission = useCheckProjectPermission(
    "PROJECT_META_FIELDS_MANAGEMENT",
  );

  const activeWorkspaceUuid = useSelector(
    (state: RootState) => state.mainReducer.activeWorkspaceUuid,
  );

  const { projectBasicData, taskType, isOpenAddBriefPane } = useSelector(
    (state: RootState) => state.projectReducer,
  );

  usePageTitle(
    projectBasicData?.projectName
      ? `${projectBasicData?.projectName} - ${capitalizeFirstLetter(taskType)}s`
      : undefined,
  );

  const hasAccessAndIsEnabledCreatorSearch =
    useHasAccessAndIsEnabledCreatorSearch();

  const handleCloseCreatorSearchTab = () => {
    setShowCreatorSearchTab(false);
  };

  const getCurrentSearchParams = () => {
    const search = new URLSearchParams(history.location.search);
    const tab = search.get("tab");
    const taskTypeParam = search.get("taskType");

    let searchParams = "";

    if (tab === "creatorSearch") {
      searchParams = "?tab=creatorSearch";
    } else if (taskTypeParam) {
      searchParams = `?taskType=${taskType}`;
    }

    return searchParams;
  };

  const searchParams = getCurrentSearchParams();

  const handleOpenActivateProjectPane = () => {
    setisActivateProjectPaneOpen(true);
  };

  const handleCloseActivateProjectPane = () => {
    setisActivateProjectPaneOpen(false);
  };

  const isActiveProject = projectBasicData?.status === "active";
  const isResearchProject = projectBasicData?.status === "research";

  const renderTopBar = (children?: ReactNode) => (
    <SingleProjectTopbar
      setBoardHidden={setBoardHidden}
      showCreatorSearchTab={showCreatorSearchTab}
      handleCloseCreatorSearchTab={handleCloseCreatorSearchTab}
      isActiveProject={isActiveProject}
      projectId={projectId}
    >
      {children}
    </SingleProjectTopbar>
  );

  useEffect(() => {
    dispatch(getProject(projectId));
  }, [projectId, dispatch]);

  useEffect(() => {
    if (searchParams.includes("tab=creatorSearch")) {
      if (hasAccessAndIsEnabledCreatorSearch) {
        setShowCreatorSearchTab(true);
      } else {
        history.push({
          pathname: history.location.pathname,
          search: `?taskType=${TaskType.Creator}`,
        });
      }
    }
    if (isResearchProject && searchParams.length === 0) {
      history.push("?tab=creatorSearch");
    }
  }, [
    searchParams,
    isResearchProject,
    history,
    hasAccessAndIsEnabledCreatorSearch,
  ]);

  useEffect(() => {
    return () => {
      dispatch(clearProjectBasicData());
    };
  }, [dispatch]);

  useEffect(() => {
    if (isResearchProject) return;
    dispatch(getTasksCounters(projectId));
    return () => {
      dispatch(clearTasksCounters());
    };
  }, [dispatch, projectId, isResearchProject]);

  if (!projectBasicData) {
    return null;
  }

  return (
    <>
      <AnimatedDiv
        ref={singleProjectRef}
        className={classNames(
          "projects",
          "projects--single-project",
          {
            "projects--board": currentView === "board",
          },
          "scroll-fixed",
        )}
      >
        <Switch>
          <Route path={`${path}/details`}>
            <ProjectDetails searchParams={searchParams} />
          </Route>
        </Switch>
        {!isResearchProject && (
          <>
            <div className="projects__breadcrumbs-wrapper">
              <div className="projects__topbar">
                <div className="projects__topbar-left">
                  {isActiveProject && (
                    <Breadcrumbs>
                      <BreadcrumbsItem
                        text={
                          <IDHFormattedMessage
                            id="ws_campaigns"
                            defaultMessage="Campaigns"
                          />
                        }
                        to={`/workspace/${activeWorkspaceUuid}/projects`}
                      />
                      <BreadcrumbsItem
                        text={String(projectBasicData.projectName)}
                        to={`/workspace/${activeWorkspaceUuid}/projects/${projectBasicData.projectId}`}
                      />
                    </Breadcrumbs>
                  )}
                </div>
                <TopbarRight />
              </div>
            </div>
            <div className="projects__part-above-table">
              {isActiveProject && (
                <div className="projects__project-main-container">
                  <div className="projects__project-name-container">
                    <ProjectCover />
                    <Link
                      to={{
                        pathname: `/workspace/${activeWorkspaceUuid}/projects/${projectBasicData.projectId}/details`,
                        search: getCurrentSearchParams(),
                      }}
                    >
                      <div className="projects__project-name">
                        {projectBasicData.projectName}
                      </div>
                    </Link>
                  </div>

                  <div className="projects__overview-container">
                    {hasProjectMetaFieldsManagementPermission &&
                      !showCreatorSearchTab && (
                        <Button
                          size="thin"
                          variant="white"
                          onClick={() =>
                            projectBasicData.clientMode
                              ? setShowMemberOrClientModal(true)
                              : setShowAddProjectFieldModal(true)
                          }
                          data-qa="add-overview-button"
                        >
                          <PlusIcon fill="#D3D5E1" />
                          <IDHFormattedMessage
                            id="ws_add_overview"
                            defaultMessage="Add overview"
                          />
                        </Button>
                      )}

                    <ProjectReportDropdown />
                    <CampaignDataDropdown />
                    <ExtensionsDropdown location={AppLocation.SingleProject} />
                    <ProjectMenuDropdown
                      showCreatorSearchTab={showCreatorSearchTab}
                      data={projectBasicData}
                      singleProject
                    />
                  </div>
                </div>
              )}

              {isActiveProject && !showCreatorSearchTab && (
                <SingleProjectOverviewTableWrapper
                  data={projectBasicData}
                  singleProject
                  openAddProjectFieldModal={() =>
                    projectBasicData.clientMode
                      ? setShowMemberOrClientModal(true)
                      : setShowAddProjectFieldModal(true)
                  }
                />
              )}
            </div>
          </>
        )}

        {showCreatorSearchTab && (
          <CreatorSearchView
            renderTopBar={renderTopBar}
            singleProjectRef={singleProjectRef}
            isActivateProjectPaneOpen={isActivateProjectPaneOpen}
            handleOpenActivateProjectPane={handleOpenActivateProjectPane}
            handleCloseActivateProjectPane={handleCloseActivateProjectPane}
            isResearchProject={isResearchProject}
          />
        )}
        {!showCreatorSearchTab && isActiveProject && (
          <TasksTableView
            setBoardHidden={setBoardHidden}
            currentView={currentView}
            setCurrentView={setCurrentView}
            boardHidden={boardHidden}
            renderTopBar={renderTopBar}
          />
        )}
      </AnimatedDiv>
      {isOpenAddBriefPane && (
        <AddAIBriefPaneExistingProject
          onClose={() => dispatch(setOpenAddBriefPane(false))}
          isActivateProjectPaneOpen={isActivateProjectPaneOpen}
        />
      )}
      <SingleProjectModals
        projectId={projectBasicData?.projectId}
        showAddProjectFieldModal={showAddProjectFieldModal}
        showMemberOrClientModal={showMemberOrClientModal}
        setShowAddProjectFieldModal={setShowAddProjectFieldModal}
        setShowMemberOrClientModal={setShowMemberOrClientModal}
      />
      <WebsocketListener />
    </>
  );
}
